import { useCallback, useEffect, useState } from "react";
import { getActiveUsers } from "../../api/user";
import { roleRoutes } from "../../utils/roles";

// ─── Equipo del helpdesk ─────────────────────────────────────────────────────
// Lo comparten el selector de agente (asignar/reasignar) y las @menciones de
// las notas privadas. Reutiliza el fetch de la vista /admin/users
// (getActiveUsers, paginado server-side) con un límite alto para traer el
// equipo completo de una vez, y filtra client-side a los roles capaces de
// atender helpdesk.
//
// Caché corta (60 s) a nivel de módulo: el selector de menciones se abre con
// cada "@" y no tiene sentido volver a pedir el equipo en cada tecla; pasado
// ese tiempo, una apertura nueva trae altas y bajas recientes.

// Roles helpdesk-capaces (excluye delivery/client/holding/etc).
export const HELPDESK_ROLE_LABELS = {
  [roleRoutes.admin]: "Administrador",
  [roleRoutes.supervisor]: "Supervisor",
  [roleRoutes.support]: "Soporte",
  [roleRoutes.superadmin]: "Superadministrador",
};

const TEAM_TTL_MS = 60 * 1000;
let teamCache = null; // { at:number, team:Array }
let teamInflight = null;

export function roleIdOf(u) {
  // `role` puede venir como ObjectId string o poblado ({ _id, name }).
  return u?.role?._id || u?.role?.id || u?.role || null;
}

export function userIdOf(u) {
  return u?._id || u?.id || null;
}

export function displayNameOf(u) {
  return `${u?.name || ""} ${u?.lastName || ""}`.trim() || u?.email || "—";
}

export function initialsOf(u) {
  const full = `${(u?.name || "").charAt(0)}${(u?.lastName || "").charAt(0)}`.trim();
  return (full || (u?.email || "?").charAt(0)).toUpperCase();
}

/** Filtra por nombre o email (sin distinguir mayúsculas). */
export function filterTeam(team, query) {
  if (!team) return [];
  const q = String(query || "").trim().toLowerCase();
  if (!q) return team;
  return team.filter(
    (u) =>
      displayNameOf(u).toLowerCase().includes(q) ||
      (u?.email || "").toLowerCase().includes(q),
  );
}

async function fetchTeam(force) {
  if (!force && teamCache && Date.now() - teamCache.at < TEAM_TTL_MS) return teamCache.team;
  if (teamInflight) return teamInflight;
  teamInflight = (async () => {
    try {
      // Límite alto porque el equipo interno (admin/supervisor/soporte) es
      // pequeño y así se evita paginar acá.
      const res = await getActiveUsers(1, 10000, "", "all", "", "", "");
      if (res?.error) throw new Error("fetch agents failed");
      const docs = res?.data?.docs || res?.data?.users || [];
      const team = docs.filter((u) => HELPDESK_ROLE_LABELS[roleIdOf(u)]);
      teamCache = { at: Date.now(), team };
      return team;
    } finally {
      teamInflight = null;
    }
  })();
  return teamInflight;
}

/** Solo para tests. */
export function resetHelpdeskTeamCache() {
  teamCache = null;
  teamInflight = null;
}

/**
 * @param {{enabled?: boolean}} [options] enabled=false difiere el fetch.
 * @returns {{team: Array|null, error: boolean, reload: () => Promise<void>}}
 *   team = null mientras carga.
 */
export default function useHelpdeskTeam({ enabled = true } = {}) {
  const [team, setTeam] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async (force) => {
    setError(false);
    try {
      const list = await fetchTeam(force);
      setTeam(list);
    } catch {
      setError(true);
      setTeam([]);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setTeam(null);
    setError(false);
    fetchTeam(false)
      .then((list) => {
        if (!cancelled) setTeam(list);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setTeam([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Reintento explícito (botón "Reintentar"): salta la caché.
  const reload = useCallback(async () => {
    setTeam(null);
    await load(true);
  }, [load]);

  return { team, error, reload };
}
